import { NavLink, useNavigate } from 'react-router-dom';
import { useLogout, useMe } from '../../store/queryHooks';
import { useUiStore } from '../../store/ui';
import styles from './Sidebar.module.css';

const PRIMARY = [
  { to: '/overblik', badge: 'O', label: 'Overblik' },
  { to: '/budget', badge: 'M', label: 'Månedsbudget' },
  { to: '/indtaegter', badge: 'I', label: 'Indtægter' },
  { to: '/faste', badge: 'F', label: 'Faste udgifter' },
  { to: '/variable', badge: 'V', label: 'Variable udgifter' },
  { to: '/maal', badge: 'O', label: 'Opsparing' },
  { to: '/konti', badge: 'K', label: 'Konti' },
  { to: '/guide', badge: 'G', label: 'Guide' },
] as const;

const SECONDARY = [
  { to: '/pension', badge: 'P', label: 'Pension' },
  { to: '/gaeld', badge: 'G', label: 'Gæld' },
] as const;

const ADMIN = [{ to: '/familier', badge: 'F', label: 'Familier' }] as const;

export function Sidebar() {
  const me = useMe();
  const householdName = me.data?.name ?? '';
  const isAdmin = !!me.data?.is_admin;
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const logout = useLogout();
  const navigate = useNavigate();

  async function onLogout() {
    try {
      await logout.mutateAsync();
    } finally {
      navigate('/login', { replace: true });
    }
  }
  return (
    <aside className={`${styles.aside} ${collapsed ? styles.collapsed : ''}`} data-print="hide">
      <NavLink to="/indstillinger" className={styles.brand} title="Åbn indstillinger">
        <div className={styles.brandBadge}>B</div>
        {!collapsed && (
          <div>
            <div className={styles.brandName}>BudgetX</div>
            <div className={styles.brandSub}>{householdName}</div>
          </div>
        )}
      </NavLink>

      <button
        type="button"
        className={styles.toggleBtn}
        onClick={toggleSidebar}
        title={collapsed ? 'Vis menu' : 'Skjul menu'}
        aria-label={collapsed ? 'Vis menu' : 'Skjul menu'}
      >
        <span className={styles.toggleIcon}>{collapsed ? '»' : '«'}</span>
        {!collapsed && <span className={styles.toggleLabel}>Skjul menu</span>}
      </button>

      <nav className={styles.nav}>
        {PRIMARY.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            title={item.label}
            className={({ isActive }) =>
              isActive ? `${styles.navItem} ${styles.active}` : styles.navItem
            }
          >
            <span className={styles.navBadge}>{item.badge}</span>
            {!collapsed && <span className={styles.navLabel}>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className={styles.divider} />

      <div className={styles.nav}>
        {!collapsed && <div className={styles.sectionLabel}>Hele økonomien</div>}
        {SECONDARY.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            title={item.label}
            className={({ isActive }) =>
              isActive ? `${styles.navItem} ${styles.active}` : styles.navItem
            }
          >
            <span className={styles.navBadge}>{item.badge}</span>
            {!collapsed && (
              <>
                <span className={styles.navLabel}>{item.label}</span>
                <span className={styles.snartBadge}>SNART</span>
              </>
            )}
          </NavLink>
        ))}
      </div>

      {isAdmin && <div className={styles.divider} />}

      {isAdmin && (
        <div className={styles.nav}>
          {!collapsed && <div className={styles.sectionLabel}>Administration</div>}
          {ADMIN.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={item.label}
              className={({ isActive }) =>
                isActive ? `${styles.navItem} ${styles.active}` : styles.navItem
              }
            >
              <span className={styles.navBadge}>{item.badge}</span>
              {!collapsed && (
                <>
                  <span className={styles.navLabel}>{item.label}</span>
                  <span className={styles.adminBadge}>ADMIN</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.logoutBtn}
          onClick={onLogout}
          disabled={logout.isPending}
          title="Log ud"
          aria-label="Log ud"
        >
          <span className={styles.logoutIcon}>⎋</span>
          {!collapsed && (
            <span className={styles.navLabel}>{logout.isPending ? 'Logger ud…' : 'Log ud'}</span>
          )}
        </button>
      </div>
    </aside>
  );
}
